import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLayarLebar } from '../hooks/useLayarLebar';
import { fetchMaintenance, formatWib, type SystemMaintenance } from '../lib/api';
import { useBahasa } from '../i18n';
import { WallStatus } from './WallStatus';

// Keputusan Direktur Utama23Oktober2026: jam Sandya tetap terlihat pada header desktop/HP.
// Tanggal di atas jam pada desktop/HP; kontrol dan proses bisnis existing dipertahankan.
/**
 * Yang tersisa dari bilah atas (§G9, 23 Agustus 2026).
 *
 * Isinya dulu toggle tema dan menu profil. Keduanya pindah ke kaki sidebar
 * setelah keluhan lapangan yang tepat sasaran: bilah itu memakan ±61px tinggi
 * di setiap halaman untuk dua kendali yang dipakai beberapa kali sehari, dan
 * yang membayarnya adalah gambar kamera di `/dashboard` — halaman yang seluruh
 * alasan keberadaannya justru gambar itu (§G7 no.1).
 *
 * Yang tinggal cuma dua hal, dan keduanya tidak punya tempat lain:
 *
 * 1. **Banner sesi pinjaman (§F7.1)** — melintang penuh di semua ukuran layar.
 *    Ia hanya ada saat sesinya memang sedang dipinjam, jadi tidak memakan
 *    apa-apa di keadaan normal.
 * 2. **Pemanggil laci navigasi**, `lg:hidden`. Di ≥1024px rail-nya selalu
 *    terlihat dan bilah ini tidak dirender sama sekali — nol piksel, bukan
 *    bilah kosong setinggi satu baris.
 */
export function AppHeader({ onOpenNav }: { onOpenNav: () => void }) {
  const { user, impersonator, setViewMode } = useAuth();
  const { t } = useBahasa();
  /**
   * Wall Status ikut menumpang di baris ini pada layar sempit (§G14, revisi
   * 1 September 2026).
   *
   * Dua syarat, dan keduanya perlu:
   *
   * - **`<1024px` saja**, dan diperiksa dengan `matchMedia` alih-alih `lg:hidden`
   *   — di ≥1024px bilahnya dipasang halaman dashboard sendiri, dan dua salinan
   *   yang saling disembunyikan CSS berarti dua poll 60 detik untuk satu bilah.
   * - **Di `/dashboard` saja.** Baris ini muncul di setiap halaman, sedangkan
   *   Wall Status milik dashboard (§G14). Menampilkannya di /logs atau /cameras
   *   bukan "bonus": ia memindahkan kesimpulan tentang seluruh armada ke halaman
   *   yang sedang dipakai memeriksa satu hal, dan itu justru yang membuat orang
   *   berhenti membacanya.
   */
  const lebar = useLayarLebar();
  const { pathname } = useLocation();
  const wallStatus = !lebar && pathname === '/dashboard';

  /**
   * Pemeliharaan yang sedang berlaku (§D10.1).
   *
   * Di-poll 60 detik, bukan sekali saat mount: yang paling perlu melihat banner
   * ini adalah orang yang tab-nya sudah terbuka sejak sebelum pemeliharaan
   * dinyalakan, dan dia tidak akan memuat ulang halaman untuk mencari tahu.
   */
  const [maintenance, setMaintenance] = useState<SystemMaintenance | null>(null);
  useEffect(() => {
    if (!user) return;
    const ambil = () =>
      void fetchMaintenance()
        .then(setMaintenance)
        .catch(() => undefined);
    ambil();
    const timer = window.setInterval(ambil, 60_000);
    return () => window.clearInterval(timer);
  }, [user]);

  return (
    <>
      {/* Menyebut TIGA hal (§D10.1 no.3): apa yang sedang dikerjakan, sejak
          kapan, dan siapa. "Sedang pemeliharaan, coba lagi nanti" adalah kalimat
          yang membuat orang menelepon — dan yang ditelepon adalah orang yang
          sedang mengerjakannya.

          Biru brand, bukan amber: ini bukan kegagalan dan bukan peringatan, ini
          keadaan yang sengaja dibuat aplikasi — kosakata warna yang sama dengan
          label MAINTENANCE di tile (§G3.1). */}
      {maintenance && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 bg-accent px-3 py-1 text-center text-xs font-semibold text-accent-contrast"
        >
          <span>
            {maintenance.scope === 'storage'
              ? t('Storage maintenance — recording is paused on purpose.')
              : t('Lensa is under maintenance.')}
          </span>
          <span className="font-normal">{maintenance.message}</span>
          <span className="font-normal opacity-90">
            {t('since {waktu}, by {siapa}', {
              waktu: formatWib(maintenance.since),
              siapa: maintenance.by,
            })}
          </span>
        </div>
      )}
      {/* Tidak bisa ditutup: orang yang lupa sedang memakai akun orang lain akan
          menyalahkan aplikasi atas kamera yang "hilang", dan itu persis yang
          terjadi kalau tandanya cuma tulisan kecil di menu profil.

          Amber, bukan merah: ini keadaan yang disengaja, bukan kegagalan. */}
      {impersonator && user && (
        <div
          role="status"
          className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 bg-amber-500 px-3 py-1 text-center text-xs font-semibold text-black"
        >
          <span>
            {t('Viewing as {nama} ({username})', {
              nama: user.fullName,
              username: user.username,
            })}
          </span>
          <Link to="/profile" className="underline underline-offset-2">
            {t('Switch back to {username}', { username: impersonator.username })}
          </Link>
        </div>
      )}

      <div className={`lensa-header-row flex items-center gap-2 border-b border-line bg-surface px-3 py-1 ${user?.viewAsUser ? 'lensa-header-user-view' : ''}`}>
        <button
          type="button"
          id="nav-toggle"
          onClick={onOpenNav}
          aria-label={t('Open navigation')}
          className="flex min-h-tap min-w-tap items-center justify-center rounded-md transition-colors hover:bg-subtle lg:hidden"
        >
          <svg
            width={20}
            height={20}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {wallStatus && <WallStatus varian="ringkas" />}
        <span className="lensa-header-clock ml-auto text-muted"><span className="sandya-clock" data-sandya-clock="" data-status="waiting" title="ntp.sandya.net"><span className="sandya-clock-date" data-clock-date="">—</span><i className="sandya-clock-dot" aria-hidden="true" /><time data-clock-time="">--:--:--.--- WIB</time></span></span>

        {/* Jalan keluar dari User view (§F8.1) juga ada di kaki sidebar, tapi di
            mobile sidebar itu laci yang tertutup — dan mode ini harus mustahil
            dilupakan, bukan mustahil dilupakan setelah dua ketukan. */}
        {user?.viewAsUser && (
          <button
            type="button"
            onClick={() => void setViewMode(false)}
            className="ml-auto flex min-h-tap items-center gap-1.5 rounded-md bg-amber-500 px-3 text-sm font-semibold text-black transition-colors hover:bg-amber-400"
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M11 5 4 12l7 7M4 12h16" />
            </svg>
            {t('Back to Admin view')}
          </button>
        )}
      </div>
    </>
  );
}
